import { FC, useEffect, useRef, useState } from 'react'
import ProgrammeExplorerGeoChart from './ProgrammeExplorerGeoChart'
import ProgrammeExplorerPieChart from './ProgrammeExplorerPieChart'
import { PEBarData, PEDonutData } from '@components/chart/types'
import { useProgrammeExplorerSupporter } from '../ProgrammeExplorerSupporterProvider'
import { MetricService } from 'app/services/metric/MetricService'
import { getMetricColors } from 'app/utils/utils'
 
declare global {
  interface Window { ApexCharts?: any }
}

export const ProgrammeExplorerGeneralMetrics: FC = () => {
  const ProgrammeExplorer = useProgrammeExplorerSupporter()
  const leftColRef = useRef<HTMLDivElement | null>(null)
  const rightColRef = useRef<HTMLDivElement | null>(null)
  const normalizeVideoSourceKey = (name: string) => {
    const n = (name || '').toLowerCase().trim()
    if (n.includes('social')) return 'social'
    if (n.includes('vimeo')) return 'vimeo'
    if (n.includes('youtube')) return 'youtube'
    return n
  }

  const getVideoSourceLabel = (name: string) => {
    const key = normalizeVideoSourceKey(name)
    if (key === 'social') return 'Social Media'
    if (key === 'vimeo') return 'Vimeo'
    if (key === 'youtube') return 'YouTube'
    return name
  }
  
  const getVideoIconSrc = (name: string) => {
    const n = (name || '').toLowerCase()
    if (n.includes('social')) return '/media/svg/social-logos/x.svg'
    if (n.includes('vimeo')) return '/media/svg/metric_icons/vimeo.svg'
    if (n.includes('youtube')) return '/media/svg/social-logos/youtube.svg'
    return undefined
  }
  const getSlideTypeIconSrc = (name: string) => {
    const n = (name || '').toLowerCase().trim()
    if (n === 'others' || n === 'other') return '/media/svg/programme_explorer_supporter/OTHERS.svg'
    if (n === 'slides - other' || n === 'slides - others') return '/media/svg/programme_explorer_supporter/SLIDES - OTHERS.svg'
    if (n === 'infographic') return '/media/svg/programme_explorer_supporter/INFOGRAPHIC.svg'
    return undefined
  }

  const normalizeRegionLabel = (name: string) => {
    const value = (name || '').trim().toLowerCase()
    if (value === '(not set)') return 'Unknown'
    return name
  }
  
  const [geoTopCountries, setGeoTopCountries] = useState<PEBarData[] | null>(null)
  const [geoRegions, setGeoRegions] = useState<PEBarData[] | null>(null)
  const [specialties, setSpecialties] = useState<PEBarData[] | null>(null)
  
  const [trafficSourcesWebsite, setTrafficSourcesWebsite] = useState<PEDonutData[] | null>(null)
  const [trafficSourcesVideo, setTrafficSourcesVideo] = useState<PEDonutData[] | null>(null)
  const [trafficSourcesPodcast, setTrafficSourcesPodcast] = useState<PEDonutData[] | null>(null)
  
  const [avgVideoViews, setAvgVideoViews] = useState<number | null>(null)

  const [types, setTypes] = useState<PEDonutData[] | null>(null)

  const [loadingGeo, setLoadingGeo] = useState(false)
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [loadingSpecialties, setLoadingSpecialties] = useState(false)
  const [loadingTrafficWebsite, setLoadingTrafficWebsite] = useState(false)
  const [loadingTrafficVideo, setLoadingTrafficVideo] = useState(false)
  const [loadingAvgVideoViews, setLoadingAvgVideoViews] = useState(false)
  const [loadingTypes, setLoadingTypes] = useState(false)

  const buildBaseFilters = () => {
    let { programmes, campaigns } = ProgrammeExplorer.params.filter_filters
    if (campaigns !== undefined && campaigns.length > 0) {
      programmes = []
    }
    return {
      ...ProgrammeExplorer.params,
      filter_filters: {
        ...ProgrammeExplorer.params.filter_filters,
        programmes,
        therapeutic_areas: []
      }
    }
  }

  const loadData = (signal: AbortSignal) => {
    const baseFilters = buildBaseFilters()

    setLoadingGeo(true)
    setLoadingRegions(true)
    setLoadingSpecialties(true)
    setLoadingTrafficWebsite(true)
    setLoadingTrafficVideo(true)
    setLoadingAvgVideoViews(true)
    ProgrammeExplorer.setExportInsightsCardsReady(false)
    setLoadingTypes(true)

    setGeoTopCountries(null)
    setGeoRegions(null)
    setSpecialties(null)
    setTrafficSourcesWebsite(null)
    setTrafficSourcesVideo(null)
    setTrafficSourcesPodcast(null)
    setAvgVideoViews(null)
    setTypes(null)

    const getFilter = (group: string, overrides: any = {}, rootOverrides: any = {}) => ({
      ...baseFilters,
      ...rootOverrides,
      filter_filters: {
        ...baseFilters.filter_filters,
        group,
        ...overrides
      }
    })

    new MetricService().getMetricEndpointData(getFilter('country', { aggregation: 'percentage' }), 'website_visits', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      const mappedData = payload.map((item: any) => ({
        name: item.group || item.name || 'unknown',
        color: getMetricColors(0),
        data: item.total ?? item.value ?? 0
      }))
      const sortedAndSliced = mappedData.sort((a: any, b: any) => b.data - a.data).slice(0, 10)
      setGeoTopCountries(sortedAndSliced)
    }).catch(() => setGeoTopCountries([])).finally(() => setLoadingGeo(false))

    new MetricService().getMetricEndpointData(getFilter('region', { aggregation: 'percentage' }), 'website_visits', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      setGeoRegions(payload.map((item: any) => ({
        name: normalizeRegionLabel(item.group || item.name || 'unknown'),
        color: getMetricColors(0),
        data: item.total ?? item.value ?? 0
      })))
    }).catch(() => setGeoRegions([])).finally(() => setLoadingRegions(false))

    new MetricService().getMetricEndpointData(getFilter('specialty', { aggregation: 'percentage' }), 'website_visits', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      setSpecialties(payload.map((item: any, idx: number) => ({
        name: item.group || item.name || 'unknown',
        color: getMetricColors(idx),
        data: item.total ?? item.value ?? 0
      })))
    }).catch(() => setSpecialties([])).finally(() => setLoadingSpecialties(false))

    new MetricService().getMetricEndpointData(getFilter('channel', { aggregation: 'percentage' }), 'website_visits', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      setTrafficSourcesWebsite(payload.map((item: any, idx: number) => ({
        name: item.group || item.name || 'unknown',
        color: getMetricColors(idx),
        data: item.total ?? item.value ?? 0
      })))
    }).catch(() => setTrafficSourcesWebsite([])).finally(() => setLoadingTrafficWebsite(false))

    new MetricService().getMetricEndpointData(getFilter('video_source', { aggregation: 'percentage' }), 'video_views', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      setTrafficSourcesVideo(payload.map((item: any, idx: number) => ({
        name: item.group || item.name || 'unknown',
        color: getMetricColors(idx),
        data: item.total ?? item.value ?? 0
      })))
    }).catch(() => setTrafficSourcesVideo([])).finally(() => setLoadingTrafficVideo(false))

    /*
    new MetricService().getMetricEndpointData(getFilter('podcast_source', { aggregation: 'percentage' }), 'podcast_listens', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      setTrafficSourcesPodcast(payload.map((item: any, idx: number) => ({
        name: item.group || item.name || 'unknown',
        color: getMetricColors(idx),
        data: item.total ?? item.value ?? 0
      })))
    }).catch(() => setTrafficSourcesPodcast([])).finally(() => setLoadingTrafficPodcast(false))
    */

    new MetricService().getMetricEndpointData(getFilter('file_type', { aggregation: 'percentage' }), 'slide_downloads', 'group', signal).then(res => {
      const payload = res.getResponseData()?.data || []
      setTypes(payload.map((item: any, idx: number) => ({
        name: item.group || item.name || 'unknown',
        color: getMetricColors(idx),
        data: item.total ?? item.value ?? 0
      })))
    }).catch(() => setTypes([])).finally(() => setLoadingTypes(false))

    new MetricService().getMetricEndpointData(getFilter('video_source'), 'video_avg_watched', 'total', signal).then(res => {
      const payload = res.getResponseData()?.data
      let val = 0;
      if (Array.isArray(payload) && payload.length > 0) {
        val = payload[0].total ?? payload[0].value ?? 0;
      } else if (payload?.total !== undefined || payload?.value !== undefined) {
        val = payload.total ?? payload.value;
      } else if (typeof payload === 'number' || typeof payload === 'string') {
        val = Number(payload);
      }
      setAvgVideoViews(Math.round(val));
    }).catch(() => setAvgVideoViews(0)).finally(() => setLoadingAvgVideoViews(false))
  }

  useEffect(() => {
    const abortController = new AbortController()
    loadData(abortController.signal)
    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ProgrammeExplorer.params])

  useEffect(() => {
    let cancelled = false
    const captureWithRetry = async (maxTries = 20, waitMs = 150) => {
      let tries = 0
      while (!cancelled && tries < maxTries) {
        try {
          if (window.ApexCharts && specialties && specialties.length > 0) {
            const res = await window.ApexCharts.exec('pe_pie_specialties', 'dataURI')
            const imgURI = res?.imgURI
            console.log('[Export][Pie] try', tries + 1, 'dataURI length:', imgURI ? imgURI.length : 0)
            if (imgURI && imgURI.length > 0) {
              console.log('[Export][Pie] dataURI ready. Length:', imgURI.length, 'Preview:', imgURI.slice(0, 40))
              ProgrammeExplorer.updateExportImage('specialties_pie', imgURI)
              return
            }
          }
        } catch (e) {
          console.log('[Export][Pie] Error getting dataURI at try', tries + 1, e)
        }
        tries++
        await new Promise(r => setTimeout(r, waitMs))
      }
      console.log('[Export][Pie] dataURI not available after retries')
    }
    captureWithRetry()
    return () => { cancelled = true }
  }, [specialties])

  useEffect(() => {
    const engagementInsightsLoaded =
      !loadingTrafficWebsite &&
      !loadingTrafficVideo &&
      !loadingTypes &&
      !loadingAvgVideoViews

    if (!engagementInsightsLoaded) {
      return
    }

    const allowed = ['social', 'email', 'search']
    const website = (trafficSourcesWebsite || [])
      .map((s) => ({ name: (s.name || '').toString(), key: (s.name || '').toLowerCase().trim(), data: s.data }))
      .filter((s) => allowed.includes(s.key))
      .map((s) => ({ name: s.key === 'social' ? 'Social Media' : s.key === 'email' ? 'Newsletter' : 'Search', value: s.data }))
    const video = (trafficSourcesVideo || [])
      .filter((s) => ['vimeo', 'youtube', 'social'].includes(normalizeVideoSourceKey(String(s.name || ''))))
      .map((s) => ({ name: getVideoSourceLabel(s.name), value: s.data, icon: getVideoIconSrc(String(s.name || '')) }))
    const podcast = (trafficSourcesPodcast || []).map((s) => ({ name: 'Podcast', value: s.data }))
    const fileTypes = (types || []).map((t) => ({ name: t.name, value: t.data }))
    const nameMedia = ((ProgrammeExplorer.params.filter_filters as any)?.name_media) || []
    const engagementChannelSubtitle = nameMedia.length > 0
      ? `${String(nameMedia[0])} Sources`
      : 'Video Sources'

    ProgrammeExplorer.updateExportInsights({
      website_sources: website,
      video_sources: video,
      podcast_sources: podcast,
      file_types: fileTypes,
      avg_video_views: avgVideoViews ?? 0,
      engagement_channel_subtitle: engagementChannelSubtitle,
      engagement_insights_ready: true,
    })
    ProgrammeExplorer.setExportInsightsCardsReady(true)
  }, [loadingTrafficWebsite, loadingTrafficVideo, loadingTypes, loadingAvgVideoViews, trafficSourcesWebsite, trafficSourcesVideo, trafficSourcesPodcast, types, avgVideoViews])

  useEffect(() => {
    if (geoTopCountries && geoTopCountries.length > 0) {
      const top10 = geoTopCountries.slice(0, 10).map((c) => ({ name: c.name, value: c.data }))
      ProgrammeExplorer.updateExportInsights({
        top10_countries: top10
      })
    }
  }, [geoTopCountries])

  useEffect(() => {
    if (geoRegions && geoRegions.length > 0) {
      const stats = geoRegions.map((r) => ({ name: r.name, value: r.data }))
      ProgrammeExplorer.updateExportInsights({
        regions_stats: stats
      })
    }
  }, [geoRegions])

  useEffect(() => {
    if (specialties && specialties.length > 0) {
      const legend = specialties.map((s) => ({ name: s.name, value: s.data, color: s.color }))
      ProgrammeExplorer.updateExportInsights({
        specialties_legend: legend
      })
    }
  }, [specialties])

  return (
    <div className='card mt-8 p-8'>
      <h2 className='fs-2 fw-bolder text-uppercase mb-8'>ADDITIONAL INSIGHTS:</h2>
      
      <div className='row'>
        {/* Left Column - Geography */}
        <div className='col-lg-8 pe-lg-10 border-end' ref={leftColRef}>
          <h3 className='fs-3 fw-bolder mb-2'>Where are engaged HCPs located?</h3>
          <p className='text-muted fw-semibold mb-6'>% of landing page visits</p>
          
          <div className='mb-8'>
            {loadingGeo || loadingRegions ? (
              <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 220 }}>
                <span className="spinner-border text-primary" role="status"></span>
              </div>
            ) : (
              <ProgrammeExplorerGeoChart 
                countries={geoTopCountries || []} 
                regions={geoRegions || []} 
                onImageReady={(uri) => {
                  if (uri) {
                    ProgrammeExplorer.updateExportImage('geo_map', uri)
                  }
                }}
                onTop10ImageReady={(uri) => {
                  if (uri) {
                    ProgrammeExplorer.updateExportImage('top10_countries_line', uri)
                  }
                }}
              />
            )}
          </div>

      
        </div>

        {/* Right Column - Specialties & Sources */}
        <div className='col-lg-4 ps-lg-10 mt-8 mt-lg-0' ref={rightColRef}>
          {/* Specialties */}
          <div className='mb-10'>
            <h3 className='fs-4 fw-bolder mb-6'>Which specialties do engaged HCPs represent?</h3>
            {loadingSpecialties ? (
              <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 220 }}>
                <span className="spinner-border text-primary" role="status"></span>
              </div>
            ) : (
              <div className='d-flex align-items-center'>
                <div className='me-4' style={{ width: '180px', flexShrink: 0 }}>
                  <ProgrammeExplorerPieChart 
                    data={(specialties || []).map((s) => ({ name: s.name, color: s.color, data: s.data }))} 
                    showLegend={false}
                    height={180}
                    chartId='pe_pie_specialties'
                  />
                </div>
                <div className='flex-grow-1 overflow-auto pe-2' style={{ maxHeight: '200px' }}>
                  <ul className='list-unstyled mb-0'>
                    {(specialties || []).map((s, i) => (
                      <li key={i} className='d-flex align-items-center mb-2'>
                        <span className='bullet bullet-dot h-10px w-10px me-3' style={{ backgroundColor: s.color, flexShrink: 0 }}></span>
                        <span className='fw-semibold text-gray-700 me-auto text-uppercase' style={{ fontSize: '0.80rem' }}>{s.name}:</span>
                        <span className='fw-bold ms-2' style={{ fontSize: '0.85rem' }}>{s.data}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* How do they find us? */}
          <div className='mb-10'>
            <h3 className='fs-4 fw-bolder mb-6'>How do engaged HCPs find us?</h3>
            <div className='d-flex flex-wrap gap-3 justify-content-between'>
              {loadingTrafficWebsite && (
                <div className='w-100 d-flex justify-content-center py-10'>
                  <span className="spinner-border text-primary" role="status"></span>
                </div>
              )}
              {(() => {
                const allowed = ['social', 'email', 'search']
                const order = allowed
                const mapped = (trafficSourcesWebsite || [])
                  .map((s) => ({ ...s, _key: (s.name || '').toLowerCase().trim() }))
                  .filter((s) => allowed.includes(s._key))
                  .map((s) => {
                    const label =
                      s._key === 'social' ? 'Social Media' :
                      s._key === 'email' ? 'Newsletter' :
                      'Search'
                    const icon =
                      s._key === 'social'
                        ? '/media/svg/programme_explorer_supporter/heart.svg'
                        : s._key === 'email'
                        ? '/media/svg/programme_explorer_supporter/laptop.svg'
                        : '/media/svg/programme_explorer_supporter/search.svg'
                    return { ...s, label, icon }
                  })
                  .sort((a: any, b: any) => order.indexOf(a._key) - order.indexOf(b._key))

                return mapped.map((source: any, i: number) => (
                  <div key={i} className='card border border-2 border-gray-300 flex-grow-1' style={{ minWidth: '30%', maxWidth: '45%' }}>
                    <div className='card-body p-4 text-center'>
                      <div className="mb-2">
                        <img
                          width="28"
                          height="28"
                          alt={source.label}
                          src={source.icon}
                        />
                      </div>
                      <div className='text-gray-600 fw-semibold text-capitalize' style={{ fontSize: '0.85rem', minHeight: '32px' }}>{source.label}</div>
                      <div className='fs-2 fw-bolder text-primary'>{source.data}%</div>
                    </div>
                  </div>
                ))
              })()}
              {(!trafficSourcesWebsite || trafficSourcesWebsite.length === 0) && (
                !loadingTrafficWebsite && <div className="text-muted">No data available</div>
              )}
            </div>
          </div>

          {/* Average Video Views */}
          <div className='mb-10'>
            <div className='d-flex justify-content-between flex-column py-2 rounded'>
              <h3 className='fs-5 fw-bolder mb-0' style={{ maxWidth: '400px' }}>
                Average video/podcast watched/listened ?
              </h3>
              {loadingAvgVideoViews ? (
                <div className='d-flex justify-content-center px-10'>
                  <span className="spinner-border text-primary" role="status"></span>
                </div>
              ) : (
                <div className='bg-white rounded px-10 py-2 border border-gray-300 mt-4 mx-auto'>
                  <span className='fs-2 fw-bolder text-primary'>{avgVideoViews}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Engagement by Channel */}
          <div>
            <h3 className='fs-4 fw-bolder mb-6'>Where and for how long do HCPs engage with video?</h3>
            
            {loadingTrafficVideo ? (
              <div className='d-flex justify-content-center py-6'>
                <span className="spinner-border text-primary" role="status"></span>
              </div>
            ) : (trafficSourcesVideo && trafficSourcesVideo.filter((source) => ['social', 'vimeo', 'youtube'].includes(normalizeVideoSourceKey(String(source.name || '')))).length > 0) && (
              <div className='mb-4'>
                <h6 className='fw-bolder text-muted text-uppercase mb-3'>Video Sources</h6>
                <div className='row g-4'>
                  {trafficSourcesVideo
                    .filter((source) => ['social', 'vimeo', 'youtube'].includes(normalizeVideoSourceKey(String(source.name || ''))))
                    .map((source, i) => {
                    const icon = getVideoIconSrc(source.name)
                    return (
                      <div key={i} className='col-12 col-sm-6'>
                        <div className='card border border-2 border-gray-300 h-100'>
                          <div className='card-body d-flex flex-column justify-content-between p-5'>
                            <div className='d-flex flex-column align-items-center text-center'>
                              {icon && (
                                <img src={icon} alt={getVideoSourceLabel(source.name)} width="28" height="28" className='mb-2' />
                              )}
                              <div className='fw-semibold text-gray-700 text-capitalize' style={{ fontSize: '0.85rem' }}>
                                {getVideoSourceLabel(source.name)}
                              </div>
                            </div>
                            <div className='mt-4 d-flex justify-content-center'>
                              <span className='fs-3 fw-bolder text-primary'>{source.data}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {loadingTypes ? (
              <div className='d-flex justify-content-center py-6'>
                <span className="spinner-border text-primary" role="status"></span>
              </div>
            ) : (types && types.length > 0) && (
              <div>
                <h6 className='fw-bolder text-muted text-uppercase mb-3'>Which downloads are most popular among engaged HCPs?</h6>
                <div className='row g-4'>
                  {types.map((type, i) => {
                    const icon = getSlideTypeIconSrc(type.name)
                    return (
                      <div key={i} className='col-12 col-sm-6 col-lg-4'>
                        <div className='card border border-2 border-gray-300 h-100'>
                          <div className='card-body d-flex flex-column justify-content-between p-5'>
                            <div className='d-flex flex-column align-items-center text-center'>
                              {icon && <img src={icon} alt={type.name} width="48" height="48" className='mb-2' />}
                              <div className='fw-semibold text-gray-700' style={{ fontSize: '0.95rem' }}>{type.name}</div>
                            </div>
                            <div className='mt-4 d-flex justify-content-center'>
                              <span className='fs-3 fw-bolder text-primary'>{type.data}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
